import {
  Thermometer,
  HeartPulse,
  Activity,
  Droplet,
  ShieldCheck,
  CandyCane,
  Scale,
  Salad,
  Wind,
  Download,
  FileX2,
  Stethoscope,
  CalendarClock,
} from "lucide-react";
import { useState } from "react";
import FilePreview from "../preview/FilePreview";

const PatientVitalsSection = ({ patientVitals }: { patientVitals: any }) => {
  const [selectedFileDetails, setSelectedFileDetails] = useState({
    status: false,
    type: "",
    url: "",
  });

  const vitalsData = [
    {
      label: "Weight",
      value: patientVitals?.weight,
      unit: "kg",
      icon: <Scale className="text-blue-500 w-6 h-6" />,
    },
    {
      label: "Temperature",
      value: patientVitals?.temperature,
      unit: "°C",
      icon: <Thermometer className="text-red-500 w-6 h-6" />,
    },
    {
      label: "Blood Pressure (Min)",
      value: patientVitals?.bloodPressureMin,
      unit: "mmHg",
      icon: <ShieldCheck className="text-green-500 w-6 h-6" />,
    },
    {
      label: "Blood Pressure (Max)",
      value: patientVitals?.bloodPressureMax,
      unit: "mmHg",
      icon: <ShieldCheck className="text-green-500 w-6 h-6" />,
    },
    {
      label: "Pulse Rate",
      value: patientVitals?.pulseRate,
      unit: "bpm",
      icon: <Activity className="text-orange-500 w-6 h-6" />,
    },
    {
      label: "Heart Rate",
      value: patientVitals?.heartRate,
      unit: "bpm",
      icon: <HeartPulse className="text-red-600 w-6 h-6" />,
    },
    {
      label: "SpO2",
      value: patientVitals?.spo2,
      unit: "%",
      icon: <Droplet className="text-blue-400 w-6 h-6" />,
    },
    {
      label: "Glucose",
      value: patientVitals?.glucose,
      unit: "mg/dL",
      icon: <CandyCane className="text-yellow-500 w-6 h-6" />,
    },
    {
      label: "Respiratory Rate",
      value: patientVitals?.respiratoryRate,
      unit: "breaths/min",
      icon: <Wind className="text-gray-600 w-6 h-6" />,
    },
    {
      label: "Vegetarian",
      value: patientVitals?.vegetarian,
      icon: <Salad className="text-green-500 w-6 h-6" />,
    },
    {
      label: "Pregnancy Months",
      value: patientVitals?.pregnancyMonths,
      icon: <CalendarClock className="text-gray-400 w-6 h-6" />,
    },
    {
      label: "Fetal's Heart Rate",
      value: patientVitals?.fetalHeartRate,
      icon: <HeartPulse className="text-red-600 w-6 h-6" />,
    },
  ];

  const handlePreviewFile = (status: boolean, type: string, url: string) => {
    setSelectedFileDetails({ status, type, url });
  };

  const hasDocuments = patientVitals?.ongoingMedication?.length > 0;
  const hasVitals = vitalsData.some((vital) => vital.value);

  return (
    <div className="border border-gray-300 w-[45%] max-h-[100%] flex flex-col p-4 bg-white rounded-lg shadow">
      {/* DOCUMENTS */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-3">Documents</h2>
        <div className="flex items-center gap-4 min-h-[100px] flex-wrap">
          {!hasDocuments ? (
            <div className="w-full text-center flex flex-col items-center justify-center text-gray-500 border border-dashed border-gray-300 rounded-md p-6">
              <FileX2 className="w-8 h-8 mb-2" />
              <span className="text-sm">No documents found</span>
            </div>
          ) : (
            patientVitals.ongoingMedication.map((doc: any, index: number) =>
              doc.patientVitalsImage.includes(".pdf") ? (
                <div
                  key={`doc-${index}`}
                  onClick={() => window.open(doc.patientVitalsImage, "_blank")}
                  className="relative cursor-pointer border border-gray-400 rounded-lg bg-white shadow-sm w-[80px] h-[100px] flex items-start justify-end p-2"
                >
                  <span className="text-red-500 text-xs font-bold">PDF</span>
                  {[6, 8, 10, 12, 14].map((top, idx) => (
                    <span
                      key={idx}
                      className={`absolute top-${top} left-2 border border-gray-300 w-[80%]`}
                    ></span>
                  ))}
                  <Download className="absolute bottom-2 right-2 w-3 h-3" />
                </div>
              ) : (
                <div
                  key={`doc-${index}`}
                  onClick={() =>
                    handlePreviewFile(true, "image", doc.patientVitalsImage)
                  }
                  className="cursor-pointer border border-gray-400 rounded-lg bg-white shadow-sm w-[80px] h-[100px] flex items-center justify-center"
                >
                  <img
                    src={doc.patientVitalsImage}
                    alt={`Doc ${index + 1}`}
                    className="w-14 h-14 rounded"
                  />
                </div>
              )
            )
          )}
        </div>
      </div>

      {/* VITALS */}
      <h2 className="text-xl font-bold text-gray-800 mb-3">Patient Vitals</h2>
      <div className="w-full hiddenScrollbar h-[60vh] overflow-y-auto space-y-3">
        {!hasVitals ? (
          <div className="text-center text-gray-500 border border-dashed border-gray-300 rounded-md p-6 flex flex-col items-center">
            <Stethoscope className="w-8 h-8 mb-2" />
            <span className="text-sm">No vitals recorded</span>
          </div>
        ) : (
          vitalsData.map(
            (vital, index) =>
              vital.value && (
                <div
                  key={index}
                  className="flex items-center p-3 bg-gray-100 border rounded-lg shadow-sm"
                >
                  <div className="mr-4">{vital.icon}</div>
                  <div>
                    <p className="text-sm text-gray-600">{vital.label}</p>
                    <p className="text-lg font-bold text-gray-800">
                      {vital.value} {vital.unit}
                    </p>
                  </div>
                </div>
              )
          )
        )}
      </div>

      {selectedFileDetails.status && (
        <FilePreview
          onClose={() => handlePreviewFile(false, "", "")}
          fileDetails={selectedFileDetails}
        />
      )}
    </div>
  );
};

export default PatientVitalsSection;
